import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Visibility,
  Work,
  LocationOn,
  Business,
  TrendingUp
} from '@mui/icons-material';
import { jobAPI, locationAPI, positionAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ENTITY_TYPES = {
  'Insurance': { color: '#3b82f6', icon: '🛡️' },
  'Logistics': { color: '#10b981', icon: '🚚' },
  'Real Estate': { color: '#f59e0b', icon: '🏢' },
  'Multifamily': { color: '#8b5cf6', icon: '🏘️' },
  'Hospitality': { color: '#ef4444', icon: '🏨' }
};

const EntityDetail = () => {
  const { entity } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [positions, setPositions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalLocations: 0,
    totalPositions: 0
  });
  const [loading, setLoading] = useState(false);
  const [openJobDialog, setOpenJobDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openPositionDialog, setOpenPositionDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [newLocation, setNewLocation] = useState({ name: '', city: '', state: '', country: 'USA' });
  const [newPosition, setNewPosition] = useState({ title: '', department: '', description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    company: '',
    location: '',
    position: '',
    employmentType: '',
    salaryRange: { min: '', max: '', currency: 'USD' },
    requiredSkills: '',
    experienceLevel: '',
    minExperience: '',
    maxExperience: '',
    isActive: true
  });

  const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Internship'];
  const experienceLevels = ['Entry Level', 'Mid Level', 'Senior Level', 'Executive'];

  const entityInfo = ENTITY_TYPES[entity] || { color: '#64748b', icon: '📋' };

  useEffect(() => {
    if (!entity || !ENTITY_TYPES[entity]) {
      navigate('/companies');
      return;
    }
    fetchEntityJobs();
    fetchEntityLocations();
    fetchEntityPositions();
    fetchEntityCompanies();
  }, [entity]);

  const fetchEntityJobs = async () => {
    setLoading(true);
    try {
      const response = await jobAPI.getAll({ limit: 100 });
      // Filter jobs by entity without company (entity-only jobs)
      const entityJobs = response.data.data.filter(job => 
        job.entity === entity && !job.company
      );
      setJobs(entityJobs);
      
      // Calculate stats
      const activeJobs = entityJobs.filter(job => job.isActive).length;
      setStats(prev => ({
        ...prev,
        totalJobs: entityJobs.length,
        activeJobs: activeJobs
      }));
    } catch (error) {
      showSnackbar('Failed to fetch jobs', 'error');
    }
    setLoading(false);
  };

  const fetchEntityLocations = async () => {
    try {
      const response = await locationAPI.getAll({ limit: 100 });
      // Filter locations by entity without company
      const entityLocations = response.data.data.filter(loc => 
        loc.entity === entity && !loc.company
      );
      setLocations(entityLocations);
      setStats(prev => ({ ...prev, totalLocations: entityLocations.length }));
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const fetchEntityPositions = async () => {
    try {
      const response = await positionAPI.getAll({ limit: 100 });
      // Filter positions by entity without company
      const entityPositions = response.data.data.filter(pos => 
        pos.entity === entity && !pos.company
      );
      setPositions(entityPositions);
      setStats(prev => ({ ...prev, totalPositions: entityPositions.length }));
    } catch (error) {
      console.error('Failed to fetch positions');
    }
  };

  const fetchEntityCompanies = async () => {
    try {
      const response = await companyAPI.getAll({ entity: entity, isActive: 'true' });
      setCompanies(response.data.data);
    } catch (error) {
      console.error('Failed to fetch companies');
    }
  };

  const handleOpenJobDialog = (job = null) => {
    if (job) {
      setEditMode(true);
      setCurrentJob(job);
      setFormData({
        title: job.title,
        description: job.description,
        company: job.company?._id || '',
        location: job.location?._id || '',
        position: job.position?._id || '',
        employmentType: job.employmentType,
        salaryRange: job.salaryRange || { min: '', max: '', currency: 'USD' },
        requiredSkills: job.requiredSkills?.join(', ') || '',
        experienceLevel: job.experienceLevel,
        minExperience: job.minExperience || '',
        maxExperience: job.maxExperience || '',
        isActive: job.isActive !== undefined ? job.isActive : true
      });
    } else {
      setEditMode(false);
      setCurrentJob(null);
      setFormData({
        title: '',
        description: '',
        company: '',
        location: '',
        position: '',
        employmentType: '',
        salaryRange: { min: '', max: '', currency: 'USD' },
        requiredSkills: '',
        experienceLevel: '',
        minExperience: '',
        maxExperience: '',
        isActive: true
      });
    }
    setOpenJobDialog(true);
  };

  const handleCloseJobDialog = () => {
    setOpenJobDialog(false);
    setEditMode(false);
    setCurrentJob(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle "Add New" selection
    if (name === 'location' && value === '__add_new__') {
      setOpenLocationDialog(true);
      return;
    }
    if (name === 'position' && value === '__add_new__') {
      setOpenPositionDialog(true);
      return;
    }
    
    if (name.startsWith('salary')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        salaryRange: { ...formData.salaryRange, [field]: value }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        ...formData,
        entity: entity,
        company: formData.company || null, // Use selected company or null for entity-only
        requiredSkills: formData.requiredSkills.split(',').map(s => s.trim()).filter(s => s)
      };
      
      if (editMode) {
        await jobAPI.update(currentJob._id, jobData);
        showSnackbar('Job updated successfully', 'success');
      } else {
        await jobAPI.create(jobData);
        showSnackbar('Job posted successfully', 'success');
      }
      
      handleCloseJobDialog();
      fetchEntityJobs();
    } catch (error) {
      showSnackbar(error.response?.data?.message || `Failed to ${editMode ? 'update' : 'post'} job`, 'error');
    }
  };

  const handleViewJob = (job) => {
    setCurrentJob(job);
    setOpenViewDialog(true);
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await jobAPI.delete(jobId);
        showSnackbar('Job deleted successfully', 'success');
        fetchEntityJobs();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Delete failed', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddLocation = async () => {
    try {
      const locationData = {
        ...newLocation,
        entity: entity,
        company: null
      };
      const response = await locationAPI.create(locationData);
      showSnackbar('Location added successfully', 'success');
      setOpenLocationDialog(false);
      setNewLocation({ name: '', city: '', state: '', country: 'USA' });
      await fetchEntityLocations();
      // Set the newly created location as selected
      setFormData({ ...formData, location: response.data.data._id });
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to add location', 'error');
    }
  };

  const handleAddPosition = async () => {
    try {
      const positionData = {
        ...newPosition,
        entity: entity,
        company: null
      };
      const response = await positionAPI.create(positionData);
      showSnackbar('Position added successfully', 'success');
      setOpenPositionDialog(false);
      setNewPosition({ title: '', department: '', description: '' });
      await fetchEntityPositions();
      // Set the newly created position as selected
      setFormData({ ...formData, position: response.data.data._id });
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to add position', 'error');
    }
  };

  const columns = [
    { field: 'title', headerName: 'Job Title', flex: 1, minWidth: 200 },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.location?.name || 'N/A'
    },
    {
      field: 'position',
      headerName: 'Position',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.position?.title || 'N/A'
    },
    {
      field: 'employmentType',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" />
      )
    },
    {
      field: 'experienceLevel',
      headerName: 'Experience',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="secondary" />
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewJob(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <>
              <Tooltip title="Edit Job">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenJobDialog(params.row)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteJob(params.row._id)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/companies')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4">
            {entityInfo.icon} {entity}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Entity-level jobs (not assigned to specific companies)
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenJobDialog}
            sx={{ backgroundColor: entityInfo.color }}
          >
            Post New Job
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Total Jobs</Typography>
                  <Typography variant="h4">{stats.totalJobs}</Typography>
                </Box>
                <Work sx={{ fontSize: 40, opacity: 0.3, color: entityInfo.color }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Active Jobs</Typography>
                  <Typography variant="h4" color="success.main">{stats.activeJobs}</Typography>
                </Box>
                <TrendingUp color="success" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Locations</Typography>
                  <Typography variant="h4">{stats.totalLocations}</Typography>
                </Box>
                <LocationOn color="info" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">Positions</Typography>
                  <Typography variant="h4">{stats.totalPositions}</Typography>
                </Box>
                <Business color="secondary" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Jobs Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">{entity} Entity Jobs</Typography>
          <Typography variant="body2" color="text.secondary">
            Jobs posted directly under {entity} entity without specific company assignment
          </Typography>
        </Box>
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={jobs}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            getRowId={(row) => row._id}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer'
              }
            }}
          />
        </Box>
      </Paper>

      {/* Post/Edit Job Dialog */}
      <Dialog open={openJobDialog} onClose={handleCloseJobDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Job' : `Post New Job for ${entity} Entity`}</DialogTitle>
        <form onSubmit={handleSubmitJob}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This job will be {editMode ? 'updated' : 'posted'} under <strong>{entity}</strong> entity
                  {formData.company ? ' for a specific company' : ' without a specific company assignment'}.
                </Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Entity Type"
                  fullWidth
                  value={entity}
                  disabled
                  helperText="Auto-selected from current entity"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="company"
                  label="Company (Optional)"
                  fullWidth
                  value={formData.company}
                  onChange={handleChange}
                  helperText="Leave empty to post under entity only"
                >
                  <MenuItem value="">
                    <em>None - Post under Entity only</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  name="title"
                  label="Job Title"
                  fullWidth
                  required
                  value={formData.title}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Job Description"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="location"
                  label="Location"
                  fullWidth
                  required
                  value={formData.location}
                  onChange={handleChange}
                  helperText={locations.length === 0 ? `No entity-only locations available for ${entity}` : `${locations.length} location(s) available`}
                >
                  <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    + Add New Location
                  </MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc._id} value={loc._id}>
                      {loc.name} - {loc.city || 'N/A'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="position"
                  label="Position"
                  fullWidth
                  required
                  value={formData.position}
                  onChange={handleChange}
                  helperText={positions.length === 0 ? `No entity-only positions available for ${entity}` : `${positions.length} position(s) available`}
                >
                  <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    + Add New Position
                  </MenuItem>
                  {positions.map((pos) => (
                    <MenuItem key={pos._id} value={pos._id}>
                      {pos.title} - {pos.department || 'N/A'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="employmentType"
                  label="Employment Type"
                  fullWidth
                  required
                  value={formData.employmentType}
                  onChange={handleChange}
                >
                  {employmentTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="experienceLevel"
                  label="Experience Level"
                  fullWidth
                  required
                  value={formData.experienceLevel}
                  onChange={handleChange}
                >
                  {experienceLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="salary.min"
                  label="Min Salary"
                  type="number"
                  fullWidth
                  value={formData.salaryRange.min}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="salary.max"
                  label="Max Salary"
                  type="number"
                  fullWidth
                  value={formData.salaryRange.max}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="salary.currency"
                  label="Currency"
                  fullWidth
                  value={formData.salaryRange.currency}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="minExperience"
                  label="Min Experience (years)"
                  type="number"
                  fullWidth
                  value={formData.minExperience}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="maxExperience"
                  label="Max Experience (years)"
                  type="number"
                  fullWidth
                  value={formData.maxExperience}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="requiredSkills"
                  label="Required Skills (comma-separated)"
                  fullWidth
                  value={formData.requiredSkills}
                  onChange={handleChange}
                  placeholder="JavaScript, React, Node.js"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="isActive"
                  label="Job Status"
                  fullWidth
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseJobDialog}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: entityInfo.color }}>
              {editMode ? 'Update Job' : 'Post Job'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Quick Add Location Dialog */}
      <Dialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Location</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Location Name"
                fullWidth
                required
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                fullWidth
                required
                value={newLocation.city}
                onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="State"
                fullWidth
                required
                value={newLocation.state}
                onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Country"
                fullWidth
                value={newLocation.country}
                onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                This location will be created for <strong>{entity}</strong> entity without a specific company.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLocationDialog(false)}>Cancel</Button>
          <Button onClick={handleAddLocation} variant="contained" disabled={!newLocation.name || !newLocation.city || !newLocation.state}>
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Position Dialog */}
      <Dialog open={openPositionDialog} onClose={() => setOpenPositionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Position</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Position Title"
                fullWidth
                required
                value={newPosition.title}
                onChange={(e) => setNewPosition({ ...newPosition, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Department"
                fullWidth
                required
                value={newPosition.department}
                onChange={(e) => setNewPosition({ ...newPosition, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newPosition.description}
                onChange={(e) => setNewPosition({ ...newPosition, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                This position will be created for <strong>{entity}</strong> entity without a specific company.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPositionDialog(false)}>Cancel</Button>
          <Button onClick={handleAddPosition} variant="contained" disabled={!newPosition.title || !newPosition.department}>
            Add Position
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Job Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Job Details</DialogTitle>
        <DialogContent>
          {currentJob && (
            <Box>
              <Typography variant="h6" gutterBottom>{currentJob.title}</Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Entity</Typography>
                  <Chip label={currentJob.entity} size="small" sx={{ backgroundColor: entityInfo.color, color: 'white' }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                  <Typography>Entity-Only (No specific company)</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  <Typography>{currentJob.location?.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Position</Typography>
                  <Typography>{currentJob.position?.title || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Employment Type</Typography>
                  <Chip label={currentJob.employmentType} size="small" color="primary" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Experience Level</Typography>
                  <Chip label={currentJob.experienceLevel} size="small" color="secondary" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Salary Range</Typography>
                  <Typography>
                    {currentJob.salaryRange?.min && currentJob.salaryRange?.max
                      ? `${currentJob.salaryRange.currency} ${currentJob.salaryRange.min} - ${currentJob.salaryRange.max}`
                      : 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Experience Required</Typography>
                  <Typography>
                    {currentJob.minExperience && currentJob.maxExperience
                      ? `${currentJob.minExperience} - ${currentJob.maxExperience} years`
                      : 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography>{currentJob.description}</Typography>
                </Grid>
                {currentJob.requiredSkills && currentJob.requiredSkills.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Required Skills</Typography>
                    <Box sx={{ mt: 1 }}>
                      {currentJob.requiredSkills.map((skill, index) => (
                        <Chip key={index} label={skill} size="small" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EntityDetail;
